import React, { useEffect } from 'react';
import ToastErrorIcon from 'assets/toast-error.svg?react';

const DEFAULT_TOAST_DURATION = 6000;
const bannerToastDefaultOnClose = () => {};

export interface BannerToastProps {
  children: React.ReactNode;
  onClose?: () => void;
  // Matches the grow-ui Toast this replaces, where isError defaulted to true:
  // call sites signal an error by passing nothing and a success by passing false.
  isError?: boolean;
  icon?: string;
  // 0 keeps the toast up until it is dismissed.
  duration?: number;
}

const BannerToast = ({
  children,
  onClose = bannerToastDefaultOnClose,
  isError = true,
  icon = '',
  duration = DEFAULT_TOAST_DURATION
}: BannerToastProps) => {
  useEffect(() => {
    // Callers commonly mount this before they have a message, gating only on
    // their own "not yet dismissed" flag. grow-ui's Toast closed itself in
    // that case; without it an empty pill shows the icon and nothing else.
    if (!children) {
      onClose();
      return undefined;
    }
    if (!duration) {
      return undefined;
    }
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [children, duration, onClose]);

  if (!children) {
    return null;
  }

  return (
    <div
      data-testid="toast"
      role="alert"
      className="fixed z-50 top-0 inset-x-0 w-full pt-4"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss notification"
        className="flex items-center justify-center text-center mx-auto focus:outline-hidden focus:shadow-outline"
      >
        <div
          className={`flex items-center justify-center text-center m-auto w-fit px-4 py-3 text-sm font-medium rounded shadow-lg text-white ${
            isError ? 'bg-chili-red' : 'bg-cropwise-green'
          }`}
        >
          {icon === 'error' && <ToastErrorIcon />}
          <div className="block sm:inline capitalize mx-2">{children}</div>
        </div>
      </button>
    </div>
  );
};

export default BannerToast;
